import { Button, Panel, PinPad } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { hrApi, hrAttendanceApi, type PersonnelFileRow } from "../../api.js";

export function TabletTab() {
  const [selectedPerson, setSelectedPerson] = useState<PersonnelFileRow | null>(null);
  const [code, setCode] = useState("");
  const [lastResult, setLastResult] = useState<{
    success: boolean;
    personName: string;
    punchType: "in" | "out";
    punchTime: string;
    message?: string;
  } | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const staffQuery = useQuery({
    queryKey: ["hr", "people"],
    queryFn: () => hrApi.listPeople(),
  });

  const staff = staffQuery.data ?? [];

  const handlePunch = async (punchType: "in" | "out") => {
    if (!selectedPerson) return;
    if (code.length < 4) {
      setErrorMsg("کد حضور حداقل باید ۴ رقم باشد.");
      return;
    }

    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await hrAttendanceApi.punchAttendance({
        personId: selectedPerson.personId,
        code,
        punchType,
      });

      setLastResult({
        success: true,
        personName: res.personName,
        punchType: res.punchType,
        punchTime: res.punchTime,
      });

      // Reset selection and code for the next punch
      setSelectedPerson(null);
      setCode("");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "کد حضور اشتباه است یا دستگاه قفل شده است";
      setErrorMsg(msg);
      setLastResult({
        success: false,
        personName: selectedPerson.personName,
        punchType,
        punchTime: new Date().toISOString(),
        message: msg,
      });
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="stack" style={{ gap: "var(--s4)", maxWidth: "800px", margin: "0 auto" }}>
      {/* Huge confirmation banner if punch recently completed */}
      {lastResult && (
        <div
          className="card"
          style={{
            padding: "var(--s4)",
            textAlign: "center",
            backgroundColor: lastResult.success ? "#ecfdf5" : "#fef2f2",
            border: `2px solid ${lastResult.success ? "#10b981" : "#ef4444"}`,
            borderRadius: "12px",
          }}
        >
          {lastResult.success ? (
            <div className="stack" style={{ gap: "var(--s2)", alignItems: "center" }}>
              <div
                style={{
                  fontSize: "1.8rem",
                  fontWeight: "bold",
                  color: "#065f46",
                }}
              >
                ثبت {lastResult.punchType === "in" ? "«ورود»" : "«خروج»"} موفقیت‌آمیز بود
              </div>
              <div style={{ fontSize: "1.2rem", color: "#047857" }}>
                پرسنل: <strong>{lastResult.personName}</strong> — ساعت:{" "}
                <strong>
                  {new Date(lastResult.punchTime).toLocaleTimeString("fa-IR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </strong>
              </div>
              <Button
                style={{ minHeight: "48px", minWidth: "160px", marginTop: "var(--s2)" }}
                onClick={() => {
                  setLastResult(null);
                }}
              >
                ثبت تردد بعدی
              </Button>
            </div>
          ) : (
            <div className="stack" style={{ gap: "var(--s2)", alignItems: "center" }}>
              <div style={{ fontSize: "1.6rem", fontWeight: "bold", color: "#b91c1c" }}>خطا در ثبت تردد</div>
              <div style={{ fontSize: "1.1rem", color: "#dc2626" }}>
                {lastResult.message || "کد حضور نادرست است"}
              </div>
              <Button
                style={{ minHeight: "48px", minWidth: "160px", marginTop: "var(--s2)" }}
                onClick={() => {
                  setLastResult(null);
                }}
              >
                تلاش مجدد
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: "var(--s4)" }}>
        {/* Step 1: Big Name List */}
        <Panel title="۱. انتخاب نام پرسنل">
          <p className="muted" style={{ margin: "0 0 var(--s2) 0", fontSize: "0.9rem" }}>
            برای ثبت تردد، روی نام خود ضربه بزنید:
          </p>

          <div
            className="stack"
            style={{
              gap: "var(--s2)",
              maxHeight: "450px",
              overflowY: "auto",
              paddingRight: "4px",
            }}
          >
            {staff.map((person: PersonnelFileRow) => {
              const isSelected = selectedPerson?.personId === person.personId;
              return (
                <button
                  key={person.personId}
                  type="button"
                  onClick={() => {
                    setSelectedPerson(person);
                    setCode("");
                    setErrorMsg(null);
                  }}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    minHeight: "52px",
                    padding: "12px 16px",
                    borderRadius: "8px",
                    border: `2px solid ${isSelected ? "var(--accent)" : "var(--line)"}`,
                    backgroundColor: isSelected ? "var(--bg-accent, #eff6ff)" : "var(--bg-card)",
                    cursor: "pointer",
                    textAlign: "right",
                    fontSize: "1.05rem",
                  }}
                >
                  <strong>{person.personName}</strong>
                  <span className="muted" style={{ fontSize: "0.85rem" }}>
                    {person.positionName || "پرسنل"}
                  </span>
                </button>
              );
            })}
          </div>
        </Panel>

        {/* Step 2: Number Pad & Action Buttons */}
        <Panel title="۲. ورود کد و ثبت تردد">
          {selectedPerson ? (
            <div className="stack" style={{ gap: "var(--s3)", alignItems: "center" }}>
              <div
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: "8px",
                  backgroundColor: "var(--bg-accent, #eff6ff)",
                  textAlign: "center",
                  fontSize: "1.15rem",
                }}
              >
                پرسنل انتخاب‌شده: <strong>{selectedPerson.personName}</strong>
              </div>

              {/* PinPad with 48px touch targets */}
              <PinPad length={4} value={code} onChange={setCode} disabled={loading} />

              {errorMsg && (
                <div style={{ color: "var(--danger)", fontSize: "0.95rem", textAlign: "center" }}>
                  {errorMsg}
                </div>
              )}

              {/* Huge Punch Buttons (Targets >= 48px) */}
              <div className="row wrap" style={{ width: "100%", gap: "var(--s3)" }}>
                <Button
                  id="punch-in-button"
                  variant="primary"
                  disabled={code.length < 4 || loading}
                  style={{
                    flex: 1,
                    minHeight: "56px",
                    fontSize: "1.3rem",
                    fontWeight: "bold",
                    backgroundColor: "#10b981",
                    borderColor: "#059669",
                  }}
                  onClick={() => {
                    void handlePunch("in");
                  }}
                >
                  ورود
                </Button>

                <Button
                  id="punch-out-button"
                  variant="urgent"
                  disabled={code.length < 4 || loading}
                  style={{
                    flex: 1,
                    minHeight: "56px",
                    fontSize: "1.3rem",
                    fontWeight: "bold",
                    backgroundColor: "#f59e0b",
                    borderColor: "#d97706",
                  }}
                  onClick={() => {
                    void handlePunch("out");
                  }}
                >
                  خروج
                </Button>
              </div>

              <Button
                style={{ minHeight: "44px", width: "100%", marginTop: "var(--s2)" }}
                onClick={() => {
                  setSelectedPerson(null);
                  setCode("");
                  setErrorMsg(null);
                }}
              >
                انصراف و انتخاب شخص دیگر
              </Button>
            </div>
          ) : (
            <div
              className="stack"
              style={{
                alignItems: "center",
                justifyContent: "center",
                height: "350px",
                textAlign: "center",
                color: "var(--text-muted)",
              }}
            >
              <div style={{ fontSize: "1.1rem" }}>لطفاً ابتدا نام پرسنل را از لیست سمت راست انتخاب کنید.</div>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
